// File: /pages/ShuffleMaps/Explore.jsx
"use client";

import { useEffect, useMemo, useState, useRef } from "react";
import dynamic from "next/dynamic";
import styles from "./Explore.module.css";

const GoogleMap = dynamic(
  () => import("/components/Shufflemaps/Map/GoogleMap").then((m) => m.default || m),
  { ssr: false }
);

export default function ExplorePage() {
  const [categories, setCategories] = useState([]); // [{id, name}]
  const [listings, setListings] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]); // category ids
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const mapRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const run = async () => {
      try {
        const [cr, lr] = await Promise.all([
          fetch("/api/shufflemaps/getAvailableCategories"),
          fetch("/api/shufflemaps/listings/get-listings")
        ]);
        const cats = await cr.json();
        const list = await lr.json();
        if (!alive) return;
        setCategories(Array.isArray(cats) ? cats : []);
        setListings(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error("Explore load error", e);
      } finally {
        if (alive) setLoading(false);
      }
    };
    run();
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return (listings || []).filter((l) => {
      const matchesText = !ql || (l.name || "").toLowerCase().includes(ql);
      const ids = Array.isArray(l.categories)
        ? l.categories
            .map((n) => categories.find((c) => c.name === n)?.id)
            .filter((v) => v != null)
        : Array.isArray(l.categoryIds) ? l.categoryIds : [];
      const matchesCats = !selectedIds.length || ids.some((id) => selectedIds.includes(id));
      return matchesText && matchesCats;
    });
  }, [q, listings, categories, selectedIds]);

  const toggleCat = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <main className={styles.shell}>
      <div className={styles.controls}>
        <div className={styles.controlsRow}>
          <input
            className={styles.input}
            placeholder="Search by name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className={styles.count}>
            {loading ? "Loading…" : `${filtered.length} result${filtered.length === 1 ? "" : "s"}`}
          </div>
        </div>
        <div className={styles.chips}>
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`${styles.chip} ${selectedIds.includes(c.id) ? styles.chipOn : ""}`}
              onClick={() => toggleCat(c.id)}
              title={c.name}
            >
              {c.name}
            </button>
          ))}
          {selectedIds.length > 0 && (
            <button type="button" className={styles.clear} onClick={() => setSelectedIds([])}>
              Clear
            </button>
          )}
        </div>
      </div>

      <div className={styles.mapWrap}>
        <GoogleMap
          ref={mapRef}
          listings={filtered}
          selectedCity={null}
          onMarkerClick={(l) => {
            // center/focus behavior if you expose an API from GoogleMap
            mapRef.current?.panToListing?.(l);
          }}
        />
      </div>
    </main>
  );
}
